import React, { useEffect, useState } from "react";
import {
  Checkbox,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { cosAttributes } from "./cosAttributes";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import { enqueueSnackbar } from "notistack";
import AlertSaveCancel from "../AlertsDialogs/AlertSaveCancel";
import { application } from "globals/endpoints";
import FormField from "layouts/applications/wizard/components/FormField";
import MDButton from "components/MDButton";

const TypeCOS = ({ type, currentFolder, isCreated }) => {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [typeList, setTypeList] = useState([]);
  const [prevTypeList, setPrevTypeList] = useState([]);
  const [isEditing, setIsEditing] = useState(isCreated);
  const [isSaving, setIsSaving] = useState("");
  const [selectedCosId, setSelectedCosId] = useState("");

  const ITEM_HEIGHT = 48;
  const ITEM_PADDING_TOP = 8;
  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
        width: 250,
      },
    },
  };

  useEffect(() => {
    if (
      currentFolder &&
      (currentFolder.type === "inbound" || currentFolder.type === "outbound")
    ) {
      const newTypeList =
        currentFolder.type === "inbound"
          ? cosAttributes.inbound
          : cosAttributes.outbound;

      setTypeList(newTypeList);
      setSelectedCosId(currentFolder.COSid);
      // Important: wait for typeList state update before fetching data
      setTimeout(() => {
        getCosData(currentFolder.name, newTypeList);
      }, 0);
    } else {
      setSelectedCosId("");
      setTypeList([]);
    }
  }, [currentFolder]);

  // Update type list based on inbound/outbound type
  useEffect(() => {
    setTypeList(() => {
      return type === "inbound"
        ? cosAttributes.inbound
        : type === "outbound"
        ? cosAttributes.outbound
        : [];
    });
  }, [type]);

  const handleCheckBoxChange = (e) => {
    const { checked, name } = e.target;
    setTypeList((prevList) =>
      prevList.map((item) =>
        item.name === name ? { ...item, value: checked } : item
      )
    );
  };

  const handleTextFieldChange = (e) => {
    const { value, name } = e.target;
    setTypeList((prevList) =>
      prevList.map((item) =>
        item.name === name ? { ...item, value: value } : item
      )
    );
  };

  const handleEditClick = () => {
    setPrevTypeList(typeList);
    setIsEditing(true);
  };

  const handleOpenSaveCancelDialog = (e, type) => {
    setIsSaving(type);
  };

  const onAgreeDelete = (id) => {
    application
      .post(`${type}Cos/delete`, {
        id: [id],
      })
      .then((response) => {
        // fetchList(type);
        setTypeList(cosAttributes[type]);
        enqueueSnackbar(response.data.messageDetail, {
          variant: "success",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
      })
      .catch((error) => {
        enqueueSnackbar(error.response.data.messageDetail, {
          variant: "error",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
      });
  };

  const handleCosCreate = () => {
    const attributesObject = {};
    typeList.forEach(({ name, value }) => {
      attributesObject[name] = value;
    });
     
    if (!attributesObject.cos) {
     enqueueSnackbar("Please define cos name to create new cos", {
        variant: "warning",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
      return;
    }

    attributesObject.id = 0;
    const payload = attributesObject;

    const apiUrl = `${type}Cos/create`;
    application
      .post(apiUrl, payload)
      .then((response) => {
        setTypeList(cosAttributes[type]);
        enqueueSnackbar(response.data.message || "Successfully Saved Changes", {
          variant: "success",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
      })
      .catch((error) => {
        enqueueSnackbar(
          error.response?.data?.message || "Failed to save data",
          {
            variant: "error",
            anchorOrigin: { vertical: "top", horizontal: "right" },
          }
        );
      });
  };

  const handleConfirmSave = () => {
    if (isSaving === "Discard") {
      setTypeList(prevTypeList);
      setIsEditing(false);
      setIsSaving("");
      return;
    }

    if (isSaving === "Delete") {
      onAgreeDelete(selectedCosId);
      setIsEditing(false);
      setIsSaving("");
      return;
    }

    // Convert the typeList array into a flat object (key: name, value: value)
    const attributesObject = {};
    typeList.forEach(({ name, value }) => {
      attributesObject[name] = value;
    });

    // Add the selectedCosId (for update) or use 0 for create
    attributesObject.id = selectedCosId || 0;
    const payload = attributesObject;

    const apiUrl =  `${type}Cos/update`;
    application
      .post(apiUrl, payload)
      .then((response) => {
        // fetchList(type);
        enqueueSnackbar(response.data.message || "Successfully Saved Changes", {
          variant: "success",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
      })
      .catch((error) => {
        enqueueSnackbar(
          error.response?.data?.message || "Failed to save data",
          {
            variant: "error",
            anchorOrigin: { vertical: "top", horizontal: "right" },
          }
        );
      });
    setIsEditing(false);
    setIsSaving("");
  };


  const getCosData = (id, updatedTypeList) => {
    const payload = {
      currentPage: 0,
      pageSize: 10,
      sortDirection: "desc",
      sortBy: "cos",
      search: "",
      sortDataType: "string",
      advancedFilters: [
        {
          direction: "from",
          dataType: "string",
          fieldName: "cos",
          value: id,
        },
        {
          direction: "to",
          dataType: "string",
          fieldName: "cos",
          value: id,
        },
      ],
    };

    application
      .post(`${type}Cos/list`, payload)
      .then((response) => {
        const cosData = response.data?.data?.currentPageData?.find(
          (item) => item.cos === id
        );
        if (cosData) {
          const updatedTypeListFinal = (updatedTypeList || typeList).map(
            (field) => ({
              ...field,
              value:
                cosData[field.name] !== undefined
                  ? cosData[field.name]
                  : field.value,
            })
          );
          setTypeList(updatedTypeListFinal);
        } else {
          enqueueSnackbar("data not found sync class of services", {
            variant: "warning",
            anchorOrigin: { vertical: "top", horizontal: "right" },
          });
        }
      })
      .catch((error) => {
        console.error("Error fetching COS details", error);
      });
  };

  return (
    <div>
      <MDBox
        sx={{
          position: "relative",
          display: "flex",
          justifyContent: "end",
          alignItems: "center",
        }}
      >
        <MDBox
          sx={{
            backgroundColor: darkMode ? "#1e1e1e" : "#fff",
            height: "25px",
            display: "flex",
            justifyContent: "end",
            alignItems: "center",
            marginBottom: "5px",
            position: "absolute",
            top: "-3em",
          }}
        >
          {isEditing && !isCreated ? (
            <>
              {/* {selectedCOS && (
                <IconButton
                  onClick={(e) => handleOpenSaveCancelDialog(e, "Delete")}
                >
                  <DeleteIcon
                    sx={{
                      fontSize: "1.4em",
                      color: darkMode ? "#fff" : "#000",
                    }}
                  />
                </IconButton>
              )} */}
              <IconButton
                onClick={(e) => handleOpenSaveCancelDialog(e, "Save")}
              >
                <SaveIcon
                  sx={{ fontSize: "1.4em", color: darkMode ? "#fff" : "#000" }}
                />
              </IconButton>
              <IconButton
                onClick={(e) => handleOpenSaveCancelDialog(e, "Discard")}
              >
                <CloseIcon
                  sx={{ fontSize: "1.4em", color: darkMode ? "#fff" : "#000" }}
                />
              </IconButton>
            </>
          ) : (
            <IconButton onClick={handleEditClick}>
              <EditIcon
                sx={{ fontSize: "1.4em", color: darkMode ? "#fff" : "#000" }}
              />
            </IconButton>
          )}
        </MDBox>
      </MDBox>

      {isCreated && (
        <MDBox 
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "10px 20px",

          }}>
          <MDTypography>{` Create New ${
            type === "inbound" ? "Inbound" : "Outbound"
          } COS`}</MDTypography>
        </MDBox>
      )}

      {/* COS Attributes */}
      <MDBox
        sx={{
          backgroundColor: darkMode ? "#1e1e1e" : "#FFFFFF",
          display: "grid",
          gridTemplateColumns: `repeat(auto-fill,minmax(200px, 1fr))`,
          gap: 2,
          padding: "0px 20px",
        }}
      >
        {typeList.map((field, index) => (
          <MDBox
            key={index}
            sx={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              gap: 1,
            }}
          >
            {field.icon === "Checkbox" && (
              <Checkbox
                name={field.name}
                onChange={handleCheckBoxChange}
                checked={field.value || false}
                disabled={!isEditing}
              />
            )}
            {field.icon === "TextField" && (
              <FormField
                name={field.name}
                variant="standard"
                placeholder={field.name?.toUpperCase()}
                onChange={handleTextFieldChange}
                value={field.value || ""}
                disabled={!isEditing}
                label={field.name}
                sx={{ width: "60%", marginLeft: "0.5em" }}
              />
            )}
            {field.icon === "Checkbox" && (
              <MDTypography
                variant="p"
                sx={{
                  fontSize: "0.7em",
                  color: darkMode
                    ? !isEditing
                      ? "#b0b0b0"
                      : "white"
                    : !isEditing
                    ? "#6d6d6d"
                    : "black",
                }}
              >
                {field?.name?.toUpperCase()}
              </MDTypography>
            )}
          </MDBox>
        ))}
      </MDBox>

      {isCreated && (
        <MDBox
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "end",
            padding: "10px 20px",
          }}
        >
          <MDButton
            variant="gradient"
            color="success"
            size="medium"
            onClick={handleCosCreate}
            sx={{ padding: "0px 50px " }}
          >
            Submit
          </MDButton>
        </MDBox>
      )}

      {isSaving && (
        <AlertSaveCancel
          isSaving={isSaving}
          setIsSaving={setIsSaving}
          handleConfirmSave={handleConfirmSave}
          buttonText={isSaving}
          name={`${isSaving} Changes`}
        />
      )}
    </div>
  );
};

export default TypeCOS;
